import { Link, useLocation } from "react-router-dom";
import { AuthPermission } from "../../services/auth.service";
import { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../redux/store";
import { UserType } from "../../services/user.service";
import ProtectedComponent from "../ProtectedComponent/ProtectedComponent";
import { ToastContext } from "../../contexts/ToastContext/ToastContext";
import { ReleaseNotesService, ReleaseNote } from "../../services/release-notes.service";
import { openModal } from "../../redux/slices/OfficeOnboardingSlice";
import { ToastVariants } from "../../models";

const hideNavPaths = ["onboarding", "invitation"];
const adminPathsList = [
  "users",
  "offices",
  "services",
  "fees",
  "finance",
  "search",
  "forms",
  "template-listing",
  "add-edit-template",
  "create-notification",
  "notification-listing",
  "integrate-configure",
  "pss-navigate",
  "version_info",
  "feature-flags",
  "mailersend-templates",
];

export interface NavListType {
  id: string;
  title: string;
  key: string;
  permissions: AuthPermission[];
  type?: "item" | "label";
}

const LeftMenu = ({ screenWidth }: { screenWidth: number }) => {
  const toast = useContext(ToastContext);
  const dispatch = useDispatch<AppDispatch>();
  const { pathname } = useLocation();
  const location = useLocation();
  const user = useSelector((state: RootState) => state.currentUser.currentUser);
  const { isCompleted } = useSelector((state: RootState) => state.officeOnboarding);
  const isOfficeAnnualPlanActive = user?.offices && user?.offices?.length > 0 ? user?.offices[0].annualPlanActive : false;

  // Check if user is a physician/admin (not a patient) and onboarding is not complete
  const shouldDisableModules =
    user?.userType !== UserType.Patient &&
    !isCompleted &&
    user?.userType !== UserType.SystemAdmin &&
    user?.userType !== UserType.User &&
    user?.userType !== UserType.Accounting;
  const [latestRelease, setLatestRelease] = useState<ReleaseNote | null>(null);
  let tempPath = pathname;
  if (pathname.endsWith("/")) {
    tempPath = pathname.slice(0, pathname.length - 1);
  }
  let currentOfficeId = pathname.split("/")[2] || "temp";

  const adminNavList: NavListType[] = [
    {
      id: "offices",
      title: "Offices",
      key: "offices",
      permissions: [],
    },
    {
      id: "users",
      title: "Users",
      key: "users",
      permissions: [],
    },
    {
      id: "services",
      title: "Services",
      key: "services",
      permissions: [],
    },
    {
      id: "fees",
      title: "Fees",
      key: "fees",
      permissions: [],
    },
    {
      id: "finance",
      title: "Finance",
      key: "finance",
      permissions: [],
    },
    {
      id: "search",
      title: "Search",
      key: "search",
      permissions: [],
    },
    {
      id: "forms",
      title: "Forms",
      key: "forms",
      permissions: [],
    },
    {
      id: "notification",
      title: "Notifications",
      key: "notification-listing",
      permissions: [],
    },
    {
      id: "feature-flags",
      title: "Feature Flags",
      key: "feature-flags",
      permissions: [],
    },
    {
      id: "mailersend-templates",
      title: "Email Templates",
      key: "mailersend-templates",
      permissions: [],
    },
    {
      id: "@#@#",
      title: "",
      key: `#`,
      permissions: [],
    },
    {
      id: "#@#@#",
      title: "",
      key: `#`,
      permissions: [],
    },
  ];

  const userNavList: NavListType[] = [
    {
      id: "dashboard",
      title: "Dashboard",
      key: `dashboard`,
      permissions: [],
      type: "label",
    },
    {
      id: "request",
      title: "Requests",
      key: `offices/${currentOfficeId}/requests`,
      permissions: [],
    },
    {
      id: "patients",
      title: "Patients",
      key: `offices/${currentOfficeId}/patients`,
      permissions: [],
    },
    {
      id: "officeManagement",
      title: "Office Management",
      key: `officeManagement`,
      permissions: [],
      type: "label",
    },
    {
      id: "officeInfo",
      title: "Office Info",
      key: `offices/${currentOfficeId}/office-info`,
      permissions: [],
    },
    {
      id: "team",
      title: "Team",
      key: `offices/${currentOfficeId}/team`,
      permissions: [],
    },
    {
      id: "contacts",
      title: "Contacts",
      key: `offices/${currentOfficeId}/contacts`,
      permissions: [],
    },
    {
      id: "forms",
      title: "Forms",
      key: `offices/${currentOfficeId}/forms`,
      permissions: [],
    },
    {
      id: "finance",
      title: "Finance",
      key: `offices/${currentOfficeId}/finance`,
      permissions: [],
    },
    {
      id: "serviceManagement",
      title: "Service Management",
      key: `serviceManagement`,
      permissions: [],
      type: "label",
    },
    {
      id: "services",
      title: "Services",
      key: `offices/${currentOfficeId}/services`,
      permissions: [],
    },
    {
      id: "fees",
      title: "Fees",
      key: `offices/${currentOfficeId}/fees`,
      permissions: [],
    },
    {
      id: "plans",
      title: "Plans",
      key: `offices/${currentOfficeId}/plans`,
      permissions: [],
    },
    {
      id: "@#@#",
      title: "",
      key: `#`,
      permissions: [],
    },
    {
      id: "#@#@#",
      title: "",
      key: `#`,
      permissions: [],
    },
  ];

  const patientNavList: NavListType[] = [
    {
      id: "dashboard",
      title: "Dashboard",
      key: `patients/${user?.userId}/dashboard`,
      permissions: [],
    },
    {
      id: "request",
      title: "Requests",
      key: `patients/${user?.userId}/requests`,
      permissions: [],
    },
    {
      id: "documents",
      title: "Documents",
      key: `patients/${user?.userId}/documents`,
      permissions: [],
    },
    {
      id: "plans",
      title: "Annual Plan",
      key: `patients/${user?.userId}/plans`,
      permissions: [],
    },
    {
      id: "receipts",
      title: "Receipts",
      key: `patients/${user?.userId}/receipts`,
      permissions: [],
    },
    {
      id: "@#@#",
      title: "",
      key: `#`,
      permissions: [],
    },
    {
      id: "#@#@#",
      title: "",
      key: `#`,
      permissions: [],
    },
    // {
    //   id: "resources",
    //   title: "Resources",
    //   key: `patients/${user?.userId}/resources`,
    //   permissions: [],
    // },
  ];

  let navList = [];
  const pathSegments = tempPath.split("/").filter(segment => segment !== "");
  const lastSegment = pathSegments[pathSegments.length - 1] || "";
  const isVersionInfoPath = tempPath.includes("version_info");
  const pathIsOnTop = (adminPathsList.includes(lastSegment) && pathSegments.length <= 2) || isVersionInfoPath;
  if (user?.userType === UserType.Patient) {
    if (user?.offices && !user.offices[0].annualPlanActive) navList = patientNavList.filter(obj => obj.id !== "plans");
    else navList = patientNavList;
  } else {
    navList = pathIsOnTop ? adminNavList : userNavList;
  }

  useEffect(() => {
    if (user !== null) {
      if (user?.offices !== undefined && user.offices.length > 0 && user?.userType !== UserType.Patient) {
        currentOfficeId = user.offices[0].officeId ? user.offices[0].officeId : "";
      }
    }
  }, [user]);

  useEffect(() => {
    window.addEventListener("resize", () => {
      if (window.outerWidth > 1023) {
        document.getElementById("mobile-nav")?.classList.add("hidden");
      }
    });
  }, [location]);

  // Fetch latest release for version display
  useEffect(() => {
    const fetchLatestRelease = async () => {
      if (user?.userType === UserType.SystemAdmin) {
        try {
          const release = await ReleaseNotesService.getLatestRelease();
          setLatestRelease(release);
        } catch (error) {
          // Silently fail - version display is optional
        }
      }
    };
    fetchLatestRelease();
  }, [user]);

  const formatVersion = (release: ReleaseNote | null): string => {
    if (!release) return "Version";
    // Format as Version X.Y where X is version (main) and Y is release (sub)
    return `Version: ${release.version}`;
  };

  const showOfficeLink = navList.length && screenWidth < 1024 && !pathIsOnTop && user?.userType === UserType.SystemAdmin;

  return (
    <div className="flex flex-col h-full">
      <ul
        className={`sidebar-menu flex-1 overflow-y-auto overflow-x-hidden ${showOfficeLink ? "pt-0" : ""} ${
          user?.userType === UserType.SystemAdmin ? "pb-32" : ""
        }`}
      >
        {showOfficeLink ? (
          <li>
            <Link to={"offices"} className={`"text-slate-700" hover:text-docsigna-purple-dark font-medium`}>
              Offices
            </Link>
          </li>
        ) : null}
        {navList.map((item: NavListType, index: number) => {
          const current = pathname.includes(item.key);
          const excludeNav = hideNavPaths.some(v => pathname.includes(v)) || pathname === "/login";
          if (excludeNav) return null;
          if (user === undefined || user === null) return null;
          return item?.type === "label" ? (
            <li
              key={`${item.key}-${index}`}
              className="bg-docnote-green text-docsigna-purple p-5 text-xl font-bold uppercase rounded-tl-[10px] rounded-bl-[10px] my-5"
            >
              {item.title}
            </li>
          ) : (
            <li key={`${item.key}-${index}`} className={`${current ? "active" : ""} ml-4`}>
              <ProtectedComponent requiredPermission={item.permissions} key={item.id}>
                {item.key === "#" ? (
                  <button
                    onClick={() => toast?.openToast()}
                    className={`${current ? "text-docsigna-purple-dark" : "text-slate-700"} hover:text-docsigna-purple-dark font-medium`}
                  >
                    {item.title}
                  </button>
                ) : shouldDisableModules ? (
                  <Link
                    to="#"
                    onClick={e => {
                      e.preventDefault();
                      dispatch(openModal());
                      toast?.openToast("Please complete onboarding to access this feature.", 3000, ToastVariants.Warn);
                    }}
                    className={`${current ? "text-docsigna-purple-dark" : "text-slate-700"} hover:text-docsigna-purple-dark font-medium opacity-50`}
                    title="Complete onboarding to access this feature"
                  >
                    {item.title}
                  </Link>
                ) : (
                  <Link
                    to={item.key}
                    className={`${current ? "text-docsigna-purple-dark" : "text-slate-700"} hover:text-docsigna-purple-dark font-medium`}
                  >
                    {item.title}
                  </Link>
                )}
              </ProtectedComponent>
            </li>
          );
        })}
      </ul>
      {/* Version display at bottom - always visible for SystemAdmin */}
      {user?.userType === UserType.SystemAdmin && (
        <div className="ml-4 mt-auto mb-6 pb-4">
          <Link
            to="version_info"
            className={`block py-3 px-6 leading-7 text-base font-medium hover:text-docsigna-purple-dark ${
              isVersionInfoPath ? "text-docsigna-purple-dark" : "text-white"
            }`}
          >
            {formatVersion(latestRelease)}
          </Link>
        </div>
      )}
    </div>
  );
};

export default LeftMenu;
